// AmsDemo.tsx — four walk-throughs of the same household.
//
// WHAT THIS IS AND IS NOT. It is not a scripted simulation with invented data: every step links to
// the real screen, working on the real seeded tenant, and the actions it describes are the actions
// that actually exist. Nothing here fakes a result.
//
// It exists because the product's central claim is invisible on any ONE screen. Each screen looks
// like a competent version of an ordinary thing — a call log, a visit form, a case page. The claim
// is what happens BETWEEN them: that a call centre agent, a Property Officer and the Community
// Liaison Team touching the same household in the same fortnight each leave a record the others
// can read, and that the system stops the contradictory contact that follows when they cannot.
//
// So the walk-throughs are ordered to be run in sequence on one resident. Step 4 is the payoff and
// the first three exist to create what it shows.
//
// EACH ONE ALSO NAMES ITS BOUNDARY — what that role is refused, and why. A demo that only shows
// what a system permits teaches half of it, and in this module the refusals are the design.
import { Link } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'
import { can } from '../../lib/capabilities'

interface Flow {
  key: string
  persona: string
  signIn: string
  title: string
  blurb: string
  to: string
  cap?: string
  steps: string[]
  boundary: string
}

const FLOWS: Flow[] = [
  {
    key: 'call',
    persona: 'Call centre',
    signIn: 'Rahmah binte Osman',
    title: 'A resident rings in',
    blurb: 'Work a ranked list, read what the other teams already did, record what was said.',
    to: '/ams/callcentre',
    cap: 'arrears.contact.queue',
    steps: [
      'Open Call centre. Read the notice at the top: households protected by rules 1B, 1D, 1E ' +
      'and 1F are not on this list, and it says how many were removed.',
      'Pick a resident. Follow the script — the third line is the one that matters: has anything ' +
      'changed that makes paying hard right now?',
      'Choose “Says they cannot pay”. Read what the screen tells you before you save.',
      'Add who you spoke to and what they said, then save. The confirmation shows only what the ' +
      'server actually did.',
    ],
    boundary: 'An agent records a claim. They cannot set a category, suppress a letter, recommend ' +
              'or approve one. “Says they cannot pay” raises a referral for assessment — the ' +
              'difference between listening to a resident and deciding about them.',
  },
  {
    key: 'po',
    persona: 'Property Officer',
    signIn: 'Muhammad Faizal',
    title: 'Knocking on doors',
    blurb: 'A round of Categories 2–4. Collect, confirm — and hand over what is not yours.',
    to: '/ams/visits',
    cap: 'arrears.visit',
    steps: [
      'Open House visits. The round is Categories 2–4 only; hardship is not this officer’s round.',
      'Open a visit. Answer the questions in order: was anyone home, why is payment late, what ' +
      'was agreed.',
      'Choose “Hardship — pass to the CLT” and write what you saw. The note is required, because ' +
      'the CLT is being sent to that door on the strength of it.',
      'Save, and read the three effects: the case is handed over, a CLT visit is scheduled, and ' +
      'fee-bearing stages pause immediately.',
    ],
    boundary: 'A Property Officer cannot record Category 1. The server refuses it, not just the ' +
              'screen. Protection is applied on suspicion; the classification waits for evidence.',
  },
  {
    key: 'clt',
    persona: 'Community Liaison Team',
    signIn: 'Serene Tan',
    title: 'The hardship assessment',
    blurb: 'A round of Category 1. Understand, do not collect. Record the basis for the decision.',
    to: '/ams/visits',
    cap: 'arrears.hardship.assess',
    steps: [
      'Open House visits, signed in as the CLT. Every case on this round is Category 1.',
      'Open the case handed over by the Property Officer and read their note first.',
      'Record the household, what comes in, what you sighted, the driver (1A–1F), what was ' +
      'offered and who to refer to.',
      'Tick consent before recording a referral, then save. The rule for that driver is applied ' +
      'and the hand-off is closed.',
    ],
    boundary: 'A referral without recorded consent is refused. Referring a household discloses ' +
              'their circumstances to a third party, and an officer’s good intentions are not a ' +
              'lawful basis for that.',
  },
  {
    key: 'inq',
    persona: 'Anyone',
    signIn: 'any arrears role',
    title: 'Every touchpoint — the point of all three',
    blurb: 'One household, every team, one list. This is what the first three journeys produce.',
    to: '/ams/inquiry',
    cap: 'arrears.inquiry',
    steps: [
      'Open Resident inquiry and search the household you just worked — by name, block or ' +
      'account number.',
      'Read the history newest-first. Note the name against each entry, not just the team.',
      'Filter by source. “People” is the three human teams; “Automated” is everything the system ' +
      'sent by itself.',
      'Look for the juxtaposition: a demand letter recommended one month, and a call the next ' +
      'saying the account holder has been in hospital since June. Before this screen, neither ' +
      'team could see the other’s entry.',
    ],
    boundary: 'Nothing is hidden by default. A history that silently omitted what was sent to a ' +
              'resident would be worse than a long one.',
  },
]

export default function AmsDemo() {
  const { identity, me } = useAuth()

  return (
    <div className="ams">
      <header className="ams-head">
        <div>
          <h2>Guided demo</h2>
          <p className="muted">
            Four walk-throughs of the same household. Run them in order — the fourth is the point,
            and the first three create what it shows.
          </p>
        </div>
      </header>

      <div className="notice">
        <b>Switch role between journeys.</b> Each is done by a different person, and each sees a
        different, smaller app. Use <b>Switch user</b> at the foot of the menu.
        {me?.authEnabled && (
          <p>
            Authentication is switched on for this server, so switching identity is a real sign-in
            rather than a development shortcut.
          </p>
        )}
      </div>

      <div className="grid-2 demo-grid">
        {FLOWS.map((f, i) => {
          // Shown to everyone, so the whole workflow is legible — but honest about whether the
          // person reading it can actually run this one. Hiding it would teach a partial system.
          const allowed = !f.cap || can(identity, f.cap)
          return (
            <section key={f.key} className="card demo-card">
              <div className="demo-top">
                <div>
                  <div className="kpi-label">{i + 1} · {f.persona}</div>
                  <h3>{f.title}</h3>
                </div>
                {allowed
                  ? <span className="tag c2">You can run this</span>
                  : <span className="tag">Sign in as {f.signIn}</span>}
              </div>
              <p className="muted sm">{f.blurb}</p>
              <ol className="demo-steps">
                {f.steps.map((s, n) => <li key={n}>{s}</li>)}
              </ol>
              <div className="demo-boundary">
                <b>What this role cannot do</b>
                <p>{f.boundary}</p>
              </div>
              {allowed
                ? <Link className="btn pri" to={f.to}>Start — open {f.title.toLowerCase()}</Link>
                : <span className="muted xs">
                    Your role does not hold this. Switch to {f.signIn} to run it.
                  </span>}
            </section>
          )
        })}
      </div>

      <section className="card">
        <h3>How the teams hand over</h3>
        <p className="muted sm">
          Days overdue, never months — a balance of three months of charges says nothing about how
          long anyone has been overdue.
        </p>
        <div className="stages demo-flow">
          <div className="stage stage-d30">
            <b>Automated outreach</b>
            <span className="muted sm">From day 1. WhatsApp, SMS, email by risk.</span>
          </div>
          <div className="stage stage-d30">
            <b>Call centre</b>
            <span className="muted sm">30 days on. Ranked calls, promises, hardship claims.</span>
          </div>
          <div className="stage stage-d60">
            <b>Property Officer or CLT</b>
            <span className="muted sm">60 days on. PO for Categories 2–4, CLT for Category 1.</span>
          </div>
          <div className="stage stage-d90">
            <b>Legal ladder</b>
            <span className="muted sm">90 days on. Category 3, and only after approval.</span>
          </div>
        </div>
      </section>

      <div className="notice stop">
        <b>What this demo does not show.</b> Nothing is actually sent — TownForge triggers, TCMS
        sends, and the TCMS behind this is our own simulator. Referrals are recorded, not
        transmitted. The field app needs a connection. Photographs and signatures are not captured.
        Full list: <code>docs/ams-workflow-demo.md</code> §6.
      </div>
    </div>
  )
}
